// Optional instructional lesson video.
// Renders ONLY when a COMPLETE approved media package is configured on the lesson:
// a self-hosted video source (lesson.instructional_video.src), a caption file
// (.captions), AND a written transcript (.transcriptText | .transcript).
// If any of the three is missing it renders nothing at all — no container,
// placeholder, empty space, or decorative substitute. A dev-only console warning
// is emitted when a partial/incomplete package is present.
// Self-hosted / object-storage video. Click-to-play, no autoplay, no loop.
// Not tied to lesson completion or unlocking. No analytics/tracking.

export const LessonVideo = ({ video, title }) => {
  if (!video || typeof video !== "object") return null;

  const transcript = video.transcriptText || video.transcript;
  const hasSrc = !!video.src;
  const hasCaptions = !!video.captions;
  const hasTranscript = !!transcript;

  // Complete package requires all three. Render nothing otherwise.
  if (!hasSrc || !hasCaptions || !hasTranscript) {
    const partial = hasSrc || hasCaptions || hasTranscript;
    if (partial && process.env.NODE_ENV === "development") {
      const missing = [
        !hasSrc && "src",
        !hasCaptions && "captions",
        !hasTranscript && "transcript",
      ].filter(Boolean).join(", ");
      // eslint-disable-next-line no-console
      console.warn(
        `[LessonVideo] Incomplete instructional_video package for "${title || "lesson"}" — missing: ${missing}. Nothing rendered.`
      );
    }
    return null;
  }

  const label = video.title || (title ? `${title} — instructional video` : "Instructional video");

  return (
    <figure className="mb-6" data-testid="lesson-instructional-video">
      <div
        className="relative w-full rounded-sm overflow-hidden border border-border/60 bg-black"
        style={{ aspectRatio: "16 / 9" }}
      >
        <video
          className="absolute inset-0 w-full h-full"
          controls
          preload="metadata"
          playsInline
          poster={video.poster || undefined}
          aria-label={label}
          data-testid="lesson-instructional-video-player"
        >
          <source src={video.src} type={video.type || "video/mp4"} />
          <track
            kind="captions"
            src={video.captions}
            srcLang={video.captionsLang || "en"}
            label={video.captionsLabel || "English"}
            default
          />
        </video>
      </div>
      <figcaption className="mt-2">
        <details data-testid="lesson-video-transcript">
          <summary className="text-xs font-mono uppercase tracking-widest text-muted-foreground cursor-pointer">
            Transcript
          </summary>
          <div className="mt-2 text-sm text-slate-300 leading-relaxed whitespace-pre-line">
            {transcript}
          </div>
        </details>
      </figcaption>
    </figure>
  );
};
